import * as React from 'react';

import { ClientFeature } from '@frontend/app/constants';
import { useClientFeatureEnabled, useGetClientFeatureByName } from '@frontend/app/hooks';
import Text from '@frontend/app/refresh-components/Text';
import { H1 } from '@frontend/shadcn/components/typography/h1';
import { P } from '@frontend/shadcn/components/typography/p';
import { Card } from '@frontend/shadcn/components/ui/card';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { useUpdateClientFF } from '@frontend/app/hooks/useUpdateClientFF';
import { ClientFFState } from '@frontend/app/types/globalTypes';
import { message } from 'antd';
import Spinner from '@frontend/app/refresh-components/Spinner';
import { cn } from '@frontend/shadcn/lib/utils';
import { isUndefined } from 'lodash';
import { OverlaySpinner } from '@components';
import { Tag } from '@frontend/app/refresh-components';
import { useClientContext } from '@frontend/app/context';
import { useMemo } from 'react';

export interface NewFeatureSettingsProps {
  clientId: string;
}

const newFeatureSVG = require('@frontend/app/assets/svgs/newFeature.svg');

const NewFeatureSettings: React.FC<NewFeatureSettingsProps> = ({ clientId }) => {
  const { data: refreshUiFF, loading: fetchingRefreshUiFF } = useGetClientFeatureByName(ClientFeature.REFRESH_UI);
  const { data: creatorStoreFF, loading: creatorStoreLoader } = useGetClientFeatureByName(ClientFeature.SHOPIFY_LANDING_PAGE);
  const { data: budgetAllocationFF, loading: budgetAllocationLoader } = useGetClientFeatureByName(ClientFeature.BUDGET_ALLOCATION);
  const { data: budgetDashboardFF, loading: budgetDashboardLoader } = useGetClientFeatureByName(ClientFeature.BUDGET_DASHBOARD);

  const clientInfo = useClientContext();
  const isRefreshUi = useClientFeatureEnabled(ClientFeature.REFRESH_UI, { fetchPolicy: 'no-cache' });
  const isBudgetAllocation = useClientFeatureEnabled(ClientFeature.BUDGET_ALLOCATION, { fetchPolicy: 'no-cache' });
  const isBudgetDashboard = useClientFeatureEnabled(ClientFeature.BUDGET_DASHBOARD, { fetchPolicy: 'no-cache' });
  const isBudgetEnabled = isBudgetAllocation && isBudgetDashboard;

  const disableBudgetAllocationAndDashboard = useMemo(() => (
    budgetAllocationFF?.clientFeature?.state === 'new_only' &&
    budgetDashboardFF?.clientFeature?.state === 'new_only' &&
    clientInfo?.client?.createdDate > budgetAllocationFF?.clientFeature?.createdDate &&
    clientInfo?.client?.createdDate > budgetDashboardFF?.clientFeature?.createdDate
  ), [clientInfo, budgetAllocationFF, budgetDashboardFF]);

  const [successToastVisible, setSuccessToastVisible] = React.useState(false);

  const [updateClientFF, { loading: saving }] = useUpdateClientFF();

  const [loader, setLoader] = React.useState<boolean>(false);

  const loading = fetchingRefreshUiFF || creatorStoreLoader || budgetAllocationLoader || budgetDashboardLoader || saving || loader;

  const handleToggle = async (isChecked, featureFlag) => {
    setLoader(true);

    // Prepare the features update array based on which feature is being toggled
    let featuresUpdate = [];

    if (featureFlag === 'refreshUI') {
      featuresUpdate = [
        {
          id: refreshUiFF.clientFeature.id,
          state: isChecked ? ClientFFState.ADD : ClientFFState.REMOVE,
        }
      ];
    } else if (featureFlag === 'creatorStore') {
      featuresUpdate = [
        {
          id: creatorStoreFF.clientFeature.id,
          state: isChecked ? ClientFFState.ADD : ClientFFState.REMOVE,
        }
      ];
    } else if (featureFlag === 'budget') {
      featuresUpdate = [
        {
          id: budgetAllocationFF.clientFeature.id,
          state: isChecked ? ClientFFState.ADD : ClientFFState.REMOVE,
        },
        {
          id: budgetDashboardFF.clientFeature.id,
          state: isChecked ? ClientFFState.ADD : ClientFFState.REMOVE,
        }
      ];
    }

    await updateClientFF({
      variables: {
        data: {
          id: clientId,
          featuresUpdate,
        },
      },
      onCompleted: () => {
        setSuccessToastVisible(true);
        setLoader(false);
        message.success('Setting updated successfully! Page will automatically reload now.', 2, () => {
          setSuccessToastVisible(false);
          window.location.reload();
        });
      },
      onError: (e) => {
        setLoader(false);
        message.error('Something went wrong!', 2);
        console.error(`Failed to save ${featureFlag} setting. Error:`, e.message);
      },
    });
  };
  if (isUndefined(isRefreshUi)) return <OverlaySpinner />;

  // Adding shadcn class here as it must work even without FF
  return (
    <div className={cn('shadcn w-[70%] h-full font-inter relative', isRefreshUi ? 'bg-background' : '')}>
      {
        loader
        && <Spinner placement="global" />
      }
      <H1 className="text-lg font-medium">
        Explore New Features
        <img src={newFeatureSVG} className="ml-2" />
      </H1>
      <P className="text-primary">
        Explore the new features built to improve the experience for your entire team. Activate features that everyone would like to try and share your valuable feedback with us. You can revert to the previous settings whenever you wish.
      </P>
      {!disableBudgetAllocationAndDashboard &&
        <Card className="p-2xl w-full flex flex-col gap-base mt-4">
          <div className="w-full flex justify-between items-center">
            <Label htmlFor="toggle-budget-switch">
              <Text visualVariant="h4" className="font-semibold">Budget Tracking</Text>
              <Tag label="Beta" bgColor="bg-green-3" className="ml-2" />
            </Label>
            <Switch id="toggle-budget-switch" checked={isBudgetEnabled} onCheckedChange={(value) => handleToggle(value, 'budget')} disabled={loading || successToastVisible} />
          </div>
          <div className="text-textColor-secondary">
            <P> Budget Tracking on Aspire allows for brands to easily allocate and visualize spending for influencer campaigns directly from the platform.</P>
          </div>
        </Card>}
    </div>
  );
};

export default NewFeatureSettings;
